import type {
  FileTransferConfig,
  UploadProgressCallback,
  UploadResponse
} from "./types";

/**
 * Uploads a file to the API with progress tracking using XMLHttpRequest.
 * This follows industry best practices for file uploads with progress tracking.
 *
 * XMLHttpRequest is used instead of fetch() because:
 * - Native upload progress support via xhr.upload.onprogress
 * - Reliable progress tracking for uploads (fetch() doesn't support this well)
 * - Industry standard for file upload progress
 *
 * @param file - The file or blob to upload
 * @param config - Configuration with API URL, auth token, and error handling
 * @param fileAttributesHeader - Optional JSON string of file attributes
 * @param callbacks - Progress, completion, and error callbacks
 * @returns Promise<UploadResponse> - The server response containing fileId
 */
export async function uploadFileWithProgress(
  file: File | Blob,
  config: FileTransferConfig,
  fileAttributesHeader: string | undefined,
  callbacks: UploadProgressCallback
): Promise<UploadResponse> {
  const { onProgress, onComplete, onError, signal } = callbacks;

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Handle cancellation via AbortSignal
    const abortHandler = () => {
      xhr.abort();
      const error = new Error("Upload cancelled");
      error.name = "AbortError";
      onError?.(error);
      reject(error);
    };

    if (signal) {
      if (signal.aborted) {
        abortHandler();
        return;
      }
      signal.addEventListener("abort", abortHandler);
    }

    // Track upload progress
    let lastProgressUpdate = 0;
    const PROGRESS_THROTTLE_MS = 100; // Throttle updates for performance

    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) {
        const loaded = event.loaded;
        const total = event.total;
        const percentage = (loaded / total) * 100;

        // Throttle progress updates for better performance
        const now = Date.now();
        if (
          now - lastProgressUpdate > PROGRESS_THROTTLE_MS ||
          percentage === 100
        ) {
          onProgress(loaded, total, percentage);
          lastProgressUpdate = now;
        }
      }
    });

    // Handle successful upload
    xhr.addEventListener("load", () => {
      // Cleanup signal listener
      if (signal) {
        signal.removeEventListener("abort", abortHandler);
      }

      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const response = JSON.parse(xhr.responseText) as {
            data: UploadResponse;
          };
          const uploadResponse = response.data;

          // Ensure final 100% progress is sent
          onProgress(file.size, file.size, 100);

          onComplete?.(uploadResponse);
          resolve(uploadResponse);
        } catch (error) {
          const err =
            error instanceof Error
              ? error
              : new Error("Failed to parse response");
          config.onError?.(err);
          onError?.(err);
          reject(err);
        }
      } else {
        const error = new Error(`HTTP ${xhr.status}: ${xhr.statusText}`);
        config.onError?.(error);
        onError?.(error);
        reject(error);
      }
    });

    // Handle network errors
    xhr.addEventListener("error", () => {
      // Cleanup signal listener
      if (signal) {
        signal.removeEventListener("abort", abortHandler);
      }

      const error = new Error("Network error during upload");
      config.onError?.(error);
      onError?.(error);
      reject(error);
    });

    // Handle aborted uploads
    xhr.addEventListener("abort", () => {
      // Cleanup signal listener
      if (signal) {
        signal.removeEventListener("abort", abortHandler);
      }

      const error = new Error("Upload cancelled");
      error.name = "AbortError";
      onError?.(error);
      reject(error);
    });

    // Setup request
    xhr.open("POST", `${config.apiBaseUrl}/v1/files`);

    // Set headers
    xhr.setRequestHeader("Authorization", `Bearer ${config.getAccessToken()}`);
    if (fileAttributesHeader) {
      xhr.setRequestHeader(
        "X-NineamHealth-File-Attributes",
        fileAttributesHeader
      );
    }

    // Send file
    xhr.send(file);
  });
}
